import { useAsyncError } from "react-router";

import ApiError from "@/models/ApiError";

interface ApiErrorComponentProps {
  // Absent when this renders as a react-router errorElement, which reads the
  // failure through useAsyncError rather than a prop.
  error?: ApiError | string | null;
}

const supplementaryInformation = (status: ApiError["status"] | undefined) => {
  if (status === 408) {
    return "A necessary web service did not respond in a timely manner. Please try again soon.";
  }
};

// Whatever a rejected loader promise threw, reduced to something renderable.
// A loader rejects with an ApiError far more often than with an Error, and
// String() would flatten either that or a plain object to "[object Object]".
const asyncErrorText = (error: unknown): string | null => {
  if (!error) {
    return null;
  }
  if (error instanceof ApiError) {
    return error.detail_text ?? String(error.status);
  }
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "string") {
    return error;
  }
  if (typeof error === "number" || typeof error === "boolean" || typeof error === "bigint") {
    return String(error);
  }
  // Everything left, a plain object included, needs serialising rather than
  // stringifying. JSON.stringify gives undefined for a function or a symbol.
  return JSON.stringify(error) ?? "Unknown error";
};

const ApiErrorComponent = ({ error }: ApiErrorComponentProps) => {
  const async_error = useAsyncError();
  const api_error = typeof error === "object" ? error : null;
  const info = supplementaryInformation(api_error?.status);
  const summary = api_error
    ? api_error.status
    : typeof error === "string"
      ? error
      : asyncErrorText(async_error);
  return (
    <div className="mx-auto w-96 rounded bg-slate-900 p-4">
      <div className="flex gap-4">
        <div>Error:</div>
        <div>{summary}</div>
        <div>{api_error?.detail_text}</div>
      </div>
      {info && <div>{info}</div>}
    </div>
  );
};

export default ApiErrorComponent;
